import { System } from '../../../engine/ecs/core/System';
import { PacmanComponent } from '../components/PacmanComponent';
import { CollectibleComponent } from '../components/CollectibleComponent';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { SpriteComponent } from '../../../engine/ecs/components/SpriteComponent';
import { EventManager } from '../../../engine/events/EventManager';
import { Event } from '../../../engine/events/Event';
import { Entity } from '../../../engine/ecs/core/Entity';

/**
 * Collection events
 */
export class DotCollectedEvent extends Event {
  constructor(public readonly points: number) {
    super('dot_collected');
  }
}

export class PowerPelletCollectedEvent extends Event {
  constructor(public readonly points: number) {
    super('power_pellet_collected');
  }
}

/**
 * CollectionSystem - Handles collection of dots, power pellets, and bonus items
 */
export class CollectionSystem extends System {
  private eventManager: EventManager;

  constructor() {
    super();
    this.registerRequiredComponent(PacmanComponent);
    this.registerRequiredComponent(TransformComponent);
    this.eventManager = EventManager.getInstance();
  }

  public update(_deltaTime: number): void {
    // Get all Pacman entities
    const pacmanEntities = this.getEntities();

    // Get all collectible entities
    const allEntities = this.entityManager.getAllEntities();
    const collectibleEntities = allEntities.filter((entity: Entity) =>
      this.componentManager.hasComponent(entity, CollectibleComponent)
    );

    // Debug: Log occasionally
    if (Math.random() < 0.01) {
      // 1% of frames
      console.log(
        `CollectionSystem: ${pacmanEntities.length} pacman entities, ${collectibleEntities.length} collectible entities`
      );
    }

    // Check collisions between Pacman and collectibles
    for (const pacmanEntity of pacmanEntities) {
      const pacmanTransform = this.componentManager.getComponent(
        pacmanEntity,
        TransformComponent
      ) as TransformComponent;

      for (const collectibleEntity of collectibleEntities) {
        const collectible = this.componentManager.getComponent(
          collectibleEntity,
          CollectibleComponent
        ) as CollectibleComponent;

        const collectibleTransform = this.componentManager.getComponent(
          collectibleEntity,
          TransformComponent
        ) as TransformComponent;

        if (
          !collectible.isConsumed &&
          this.checkCollision(pacmanTransform, collectibleTransform)
        ) {
          console.log(
            `CollectionSystem: Collision detected! Collecting item worth ${collectible.points} points`
          );
          this.collectItem(collectible, collectibleEntity);
        }
      }
    }
  }

  /**
   * Check collision between Pacman and collectible
   */
  private checkCollision(
    pacmanTransform: TransformComponent,
    collectibleTransform: TransformComponent
  ): boolean {
    const distance = Math.sqrt(
      Math.pow(
        pacmanTransform.position.x - collectibleTransform.position.x,
        2
      ) +
        Math.pow(
          pacmanTransform.position.y - collectibleTransform.position.y,
          2
        )
    );
    return distance < 12; // Collection radius
  }

  /**
   * Collect an item and emit appropriate event
   */
  private collectItem(collectible: CollectibleComponent, entity: Entity): void {
    // Mark as consumed
    collectible.isConsumed = true;
    collectible.isVisible = false;

    // IMMEDIATELY hide the sprite so it disappears from screen
    const sprite = this.componentManager.getComponent(
      entity,
      SpriteComponent
    ) as SpriteComponent;
    if (sprite) {
      sprite.visible = false;
    }

    // Trigger Pacman eating animation
    this.triggerEatingAnimation();

    // Remove entity from game
    this.entityManager.removeEntity(entity);

    // Publish event based on collectible type
    if (this.eventManager) {
      if (collectible.isPowerPellet) {
        console.log(
          'CollectionSystem: Publishing power_pellet_collected event'
        );
        this.eventManager.publish(new Event('power_pellet_collected'));
      } else {
        console.log('CollectionSystem: Publishing dot_collected event');
        this.eventManager.publish(new Event('dot_collected'));
      }
    } else {
      console.warn(
        'CollectionSystem: No event manager available to publish events'
      );
    }

    console.log(
      `CollectionSystem: Collected ${
        collectible.isPowerPellet ? 'power pellet' : 'dot'
      } worth ${collectible.points} points`
    );
  }

  /**
   * Trigger Pacman eating animation
   */
  private triggerEatingAnimation(): void {
    const allEntities = this.entityManager.getAllEntities();
    for (const entity of allEntities) {
      if (this.componentManager.hasComponent(entity, PacmanComponent)) {
        const pacman = this.componentManager.getComponent(
          entity,
          PacmanComponent
        ) as PacmanComponent;
        pacman.isEating = true;
        pacman.eatingTimer = 0.1; // Mouth closes for 100ms
        break;
      }
    }
  }
}
